import { useState } from 'react';
import { useTonAddress } from '@tonconnect/ui-react';
import { track } from '../lib/analytics';
import { CherryMascot } from './CherryMascot';
import { LoadingButton } from './LoadingButton';
import { FormField } from './FormField';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';

type Mood = 'happy' | 'neutral' | 'frustrated';

const MOODS: { value: Mood; emoji: string; label: string }[] = [
  { value: 'happy', emoji: '😊', label: 'Happy' },
  { value: 'neutral', emoji: '😐', label: 'Neutral' },
  { value: 'frustrated', emoji: '😤', label: 'Frustrated' },
];

export function FeedbackButton() {
  const address = useTonAddress();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [mood, setMood] = useState<Mood | null>(null);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setStatus('sending');

    try {
      const res = await fetch(`${API_BASE_URL}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: message.trim(),
          mood,
          walletAddress: address || undefined,
          page: window.location.pathname,
        }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error);

      track('feedback_submitted', { mood, page: window.location.pathname, length: message.trim().length });
      setStatus('sent');
      setTimeout(() => {
        setIsOpen(false);
        setMessage('');
        setMood(null);
        setStatus('idle');
      }, 2000);
    } catch {
      setStatus('error');
    }
  };

  return (
    <>
      {/* Trigger button */}
      <button
        onClick={() => {
          setIsOpen(true);
          track('feedback_opened', { page: window.location.pathname });
        }}
        className="fixed bottom-20 right-3 z-30 w-10 h-10 rounded-full bg-cherry-500 text-white shadow-lg flex items-center justify-center hover:bg-cherry-600 active:scale-95 transition-all"
        aria-label="Send feedback"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => status !== 'sending' && setIsOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Share feedback"
            className="relative w-full max-w-sm mx-3 mb-3 sm:mb-0 bg-surface rounded-2xl shadow-(--shadow-modal) border border-border overflow-hidden animate-in slide-in-from-bottom-4"
          >
            <div className="p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-lg">Share Feedback</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  aria-label="Close feedback"
                  className="min-w-[44px] min-h-[44px] flex items-center justify-center text-text-muted hover:text-text-primary text-xl leading-none rounded-lg"
                >&times;</button>
              </div>

              {status === 'sent' ? (
                <div className="text-center py-6">
                  <div className="mb-2 flex justify-center"><CherryMascot mood="happy" size={36} /></div>
                  <p className="font-semibold">Thanks for your feedback!</p>
                  <p className="text-sm text-text-secondary mt-1">It helps us build a better P2P trading experience.</p>
                </div>
              ) : (
                <>
                  {/* Mood selector */}
                  <div className="mb-3">
                    <p className="text-sm font-medium mb-2">How are you feeling?</p>
                    <div className="flex gap-2">
                      {MOODS.map((m) => (
                        <button
                          key={m.value}
                          onClick={() => setMood(mood === m.value ? null : m.value)}
                          className={`flex-1 py-2 rounded-lg border text-center transition-all ${
                            mood === m.value
                              ? 'border-cherry-400 bg-cherry-50 scale-105'
                              : 'border-border hover:border-cherry-300'
                          }`}
                        >
                          <span className="text-xl block">{m.emoji}</span>
                          <span className="text-[0.625rem] text-text-secondary">{m.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <FormField
                    id="feedback-message"
                    label="Your message"
                    hint={`${message.length}/500${!address ? ' · Anonymous feedback' : ''}`}
                    error={status === 'error' ? "Couldn't send your feedback. Check your connection and try again." : undefined}
                  >
                    <textarea
                      id="feedback-message"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      placeholder="What's on your mind? Bugs, ideas, compliments — anything helps!"
                      maxLength={500}
                      rows={3}
                      className="w-full px-3 py-2.5 border border-border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ton-500/60 focus:border-border-focus transition-shadow"
                      aria-invalid={status === 'error' ? 'true' : undefined}
                      aria-describedby={status === 'error' ? 'error-feedback-message' : 'hint-feedback-message'}
                    />
                  </FormField>

                  <LoadingButton
                    onClick={handleSubmit}
                    disabled={!message.trim()}
                    loading={status === 'sending'}
                    loadingText="Sending..."
                    className="w-full py-2.5 bg-cherry-500 hover:bg-cherry-600 disabled:bg-disabled text-white font-bold rounded-xl transition-colors disabled:cursor-not-allowed"
                  >
                    Send Feedback
                  </LoadingButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
